import {
  RadixTooltip,
  RadixTooltipContent,
  RadixTooltipProvider,
  RadixTooltipTrigger,
  TooltipSubText,
  TooltipText,
} from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';
import {
  TooltipPosition,
  TooltipProps,
} from '../../feedback-and-status/tooltip/Tooltip';
import { cva } from 'class-variance-authority';

export interface ToolButtonProps
  extends
    React.ComponentProps<'button'>,
    Pick<TooltipProps, 'defaultOpen' | 'open' | 'onOpenChange'> {
  /** 버튼의 상태 */
  state?: 'default' | 'disabled' | 'selected';
  /** 버튼의 크기 */
  size?: 'sm' | 'md';
  /** 버튼의 아이콘 */
  children: React.ReactNode;
  /** 버튼의 툴팁 표시 여부 */
  isShowTooltip?: boolean;
  /** 버튼의 툴팁 텍스트 */
  tooltipNode?: string | React.ReactNode;
  /** 버튼의 툴팁 부가 텍스트 */
  tooltipSubNode?: string | React.ReactNode;
  /** 버튼의 툴팁 위치 */
  tooltipPosition?: TooltipPosition;
  /** 툴팁 포탈 속성 */
  portalProps?: React.ComponentProps<typeof RadixTooltipContent>['portalProps'];
  /** selected 상태에서 배경 색상을 투명하게 처리 */
  isTransparent?: boolean;
}

const ToolButonVariant = cva(
  [
    'flex cursor-pointer gap-5xs rounded-3xs bg-comp-tool-bg-default p-4xs transition-all [&>svg>g>path]:fill-comp-tool-text-default items-center',
    `hover:bg-comp-tool-bg-hover active:bg-comp-tool-bg-active`,
    '[&_path]:transition-all',
    '[[data-custom-state=selected]]:[&_path]:fill-comp-tool-text-selected',
    `[[data-custom-state=disabled]]:cursor-not-allowed [[data-custom-state=disabled]]:bg-comp-tool-bg-disabled [[data-custom-state=disabled]]:text-comp-tool-text-disabled [[data-custom-state=disabled]]:hover:bg-comp-tool-bg-disabled [[data-custom-state=disabled]]:active:bg-comp-tool-bg-disabled [[data-custom-state=disabled]]:[&>svg>g>path]:fill-comp-tool-text-disabled`,
  ],
  {
    variants: {
      size: {
        sm: ['text-caption-small', '[&_svg]:size-[20px]'],
        md: ['text-caption-medium', '[&_svg]:size-[24px]'],
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export default function ToolButton({
  state = 'default',
  size = 'md',
  isShowTooltip = false,
  tooltipNode,
  tooltipPosition = 'top',
  tooltipSubNode,
  children,
  defaultOpen,
  open,
  onClick,
  onOpenChange,
  portalProps,
  isTransparent = true,
  ...props
}: ToolButtonProps) {
  return (
    <RadixTooltipProvider delayDuration={100}>
      <RadixTooltip
        defaultOpen={defaultOpen}
        open={isShowTooltip ? open : false}
        onOpenChange={onOpenChange}
      >
        <RadixTooltipTrigger
          asChild
          {...props}
          disabled={state === 'disabled'}
          onClick={(e) => {
            if (state === 'disabled') {
              return;
            }
            onClick?.(e);
          }}
        >
          <div
            data-custom-state={state}
            className={cn(
              ToolButonVariant({ size }),
              state === 'selected' &&
                (isTransparent
                  ? 'bg-comp-tool-bg-default hover:bg-comp-tool-bg-default active:bg-comp-tool-bg-default'
                  : 'bg-comp-tool-bg-selected'),
            )}
          >
            {children}
          </div>
        </RadixTooltipTrigger>
        <RadixTooltipContent
          side={tooltipPosition}
          portalProps={portalProps}
        >
          {tooltipNode && <TooltipText>{tooltipNode}</TooltipText>}
          {tooltipSubNode && <TooltipSubText>{tooltipSubNode}</TooltipSubText>}
        </RadixTooltipContent>
      </RadixTooltip>
    </RadixTooltipProvider>
  );
}
